import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { LabelInput } from '@/common/ui/LabelInput'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { nameSchema, NameFormValues } from '../rules/nameSchema'
import { ONBOARDING_COPY } from '@/locales/en'

export type PageProps = {
    progress?: number
    defaultValues?: NameFormValues
    errors?: Partial<Record<string, string>>
    loading?: boolean
    saving?: boolean
    disabled?: boolean
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*; may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: NameFormValues) => void
}

export function NameForm({
    progress = 30,
    defaultValues,
    errors: externalErrors,
    loading,
    saving,
    disabled,
    onSubmit,
}: ViewModelProps) {
    const {
        register,
        handleSubmit,
        setFocus,
        reset,
        formState: { errors: formErrors, isValid },
    } = useForm<NameFormValues>({
        resolver: yupResolver(nameSchema),
        mode: 'onChange',
        defaultValues,
    })

    useEffect(() => {
        if (defaultValues) {
            reset(defaultValues)
        }
    }, [defaultValues, reset])

    useEffect(() => {
        setFocus('salutation')
    }, [setFocus])

    const displayErrors: PageProps['errors'] = externalErrors

    const isDisabled = disabled ?? (!isValid || loading || saving)

    return (
        <Onboarding
            title={ONBOARDING_COPY.aboutYou.title}
            isLoading={saving || loading}
            cta={ONBOARDING_COPY.next}
            description={ONBOARDING_COPY.aboutYou.description}
            onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
            disabled={isDisabled}
            progress={progress}
        >
            <div className="flex flex-col gap-6">
                <div className="flex flex-col md:flex-row gap-6">
                    <LabelInput
                        {...register('salutation')}
                        type="text"
                        id="salutation"
                        required
                        formNoValidate
                        label={ONBOARDING_COPY.aboutYou.form.salutation}
                        placeholder={ONBOARDING_COPY.aboutYou.form.salutationPlaceholder}
                        error={displayErrors?.salutation || formErrors.salutation?.message}
                    />
                    <LabelInput
                        {...register('firstName')}
                        type="text"
                        id="firstName"
                        required
                        formNoValidate
                        label={ONBOARDING_COPY.aboutYou.form.firstName}
                        placeholder={ONBOARDING_COPY.aboutYou.form.firstNamePlaceholder}
                        error={displayErrors?.firstName || formErrors.firstName?.message}
                    />
                </div>
                <div className="flex flex-col md:flex-row gap-6">
                    <LabelInput
                        {...register('middleName')}
                        type="text"
                        id="middleName"
                        formNoValidate
                        label={ONBOARDING_COPY.aboutYou.form.middleName}
                        placeholder={ONBOARDING_COPY.aboutYou.form.middleNamePlaceholder}
                        error={displayErrors?.middleName || formErrors.middleName?.message}
                    />
                    <LabelInput
                        {...register('lastName')}
                        type="text"
                        id="lastName"
                        required
                        formNoValidate
                        label={ONBOARDING_COPY.aboutYou.form.lastName}
                        placeholder={ONBOARDING_COPY.aboutYou.form.lastNamePlaceholder}
                        error={displayErrors?.lastName || formErrors.lastName?.message}
                    />
                </div>
            </div>
        </Onboarding>
    )
}
